import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import MarkdownContent, { stripMarkdown } from '../../components/MarkdownContent'
import {
  BookOpen, 
  Users, 
  Clock, 
  FileText, 
  CheckCircle, 
  ChevronDown, 
  ChevronRight,
  ArrowLeft,
  Calendar,
  Target
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import TrainerClassControl from './TrainerClassControl'

export default function CourseDetails() {
  const { courseId } = useParams()
  const [course, setCourse] = useState(null)
  const [modules, setModules] = useState([])
  const [expandedModules, setExpandedModules] = useState(new Set())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCourseDetails()
  }, [courseId])

  const fetchCourseDetails = async () => {
    try {
      setLoading(true)
      const [courseRes, modulesRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/modules`)
      ])
      
      setCourse(courseRes.data)
      const modulesArray = modulesRes.data.modules || []
      setModules(modulesArray)
      
      // Expand first module by default
      if (modulesArray.length > 0) {
        setExpandedModules(new Set([modulesArray[0].id]))
      }
    } catch (error) {
      console.error('Error fetching course details:', error)
      toast.error('Failed to load course details')
    } finally {
      setLoading(false)
    }
  }

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }

  const toggleAllModules = () => {
    if (expandedModules.size === modules.length) {
      setExpandedModules(new Set())
    } else {
      setExpandedModules(new Set(modules.map(m => m.id)))
    }
  }

  if (loading) return <LoadingSpinner label="Loading course" />

  if (!course) return (
    <div className="cinema-empty">
      <p className="font-sans text-sm text-cinema-muted mb-4">Course not found</p>
      <Link to="/trainer/courses" className="cinema-btn-primary text-xs px-4 py-2 min-h-0 inline-flex">
        Back to Courses
      </Link>
    </div>
  )

  const totalLessons = Array.isArray(modules) ? modules.reduce((sum, mod) => sum + (mod.lessons?.length || 0), 0) : 0
  const totalAssignments = Array.isArray(modules) ? modules.reduce((sum, mod) =>
    sum + (mod.lessons?.reduce((lSum, lesson) => lSum + (lesson.assignments?.length || 0), 0) || 0), 0
  ) : 0

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="cinema-page-header flex items-start gap-3">
        <Link to="/trainer/courses" className="cinema-btn-ghost p-2.5 min-h-0 shrink-0 mt-0.5">
          <ArrowLeft size={14} />
        </Link>
        <div>
          <span className="cinema-label block mb-0.5">My Courses</span>
          <h1 className="font-display text-xl sm:text-2xl text-white font-light">{course.title}</h1>
          <p className="font-sans text-xs sm:text-sm text-cinema-glow/90 mt-0.5 font-medium">{course.course_code}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
        {[
          { label: 'Modules', value: modules.length },
          { label: 'Lessons', value: totalLessons },
          { label: 'Assignments', value: totalAssignments },
          { label: 'Duration', value: course.duration || (course.duration_weeks ? `${course.duration_weeks}w` : 'N/A') },
        ].map(({ label, value }) => (
          <div key={label} className="cinema-stat">
            <span className="cinema-stat-label">{label}</span>
            <span className="cinema-stat-value">{value}</span>
          </div>
        ))}
      </div>

      {/* Description */}
      {course.description && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">About This Course</h2>
          </div>
          <p className="font-sans text-sm sm:text-base text-cinema-muted leading-relaxed">{course.description}</p>
        </div>
      )}

      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header">
          <h2 className="cinema-section-title">Class Session & AI</h2>
        </div>
        <TrainerClassControl
          courseId={courseId}
          courseTitle={course?.title}
          courseCode={course?.course_code}
        />
      </div>

      {/* Course Structure */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header flex items-center justify-between">
          <h2 className="cinema-section-title">Course Structure</h2>
          <button onClick={toggleAllModules} className="font-sans text-xs sm:text-sm text-cinema-glow hover:text-cinema-gold transition-colors font-medium">
            {expandedModules.size === modules.length ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
        <p className="font-sans text-xs sm:text-sm text-cinema-muted mb-5 leading-relaxed">
          Teaching materials are managed in{' '}
          <Link to="/trainer/content" className="text-cinema-glow hover:text-cinema-gold transition-colors font-medium">
            Upload Content
          </Link>
          {' '}→ expand a lesson.
        </p>

        {modules.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen size={32} className="mx-auto mb-3 text-cinema-muted/20" />
            <p className="font-sans text-sm text-cinema-muted">No modules available yet</p>
          </div>
        ) : (
          <div className="divide-y divide-white/4">
            {modules.map((module, moduleIndex) => (
              <div key={module.id}>
                <button onClick={() => toggleModule(module.id)}
                  className="w-full flex items-center justify-between py-4 hover:bg-white/[0.02] transition-colors text-left">
                  <div className="flex items-center gap-3">
                    {expandedModules.has(module.id)
                      ? <ChevronDown size={14} className="text-cinema-muted shrink-0" />
                      : <ChevronRight size={14} className="text-cinema-muted shrink-0" />}
                    <span className="w-6 h-6 bg-cinema-glow/15 border border-cinema-glow/30 flex items-center justify-center font-display text-xs text-cinema-glow shrink-0 font-medium">
                      {module.module_number || moduleIndex + 1}
                    </span>
                    <div>
                      <p className="font-sans text-sm sm:text-base text-white font-medium">{module.title}</p>
                      {module.description && <p className="font-sans text-xs text-cinema-muted mt-0.5">{module.description}</p>}
                    </div>
                  </div>
                  <span className="font-sans text-xs text-cinema-muted shrink-0 ml-3 font-medium">
                    {module.lessons?.length || 0} lessons
                  </span>
                </button>

                {expandedModules.has(module.id) && module.lessons && module.lessons.length > 0 && (
                  <div className="border-t border-white/4 divide-y divide-white/3 bg-white/1">
                    {module.lessons.map((lesson, lessonIndex) => (
                      <div key={lesson.id} className="px-3 py-3.5 pl-10 hover:bg-white/[0.02] transition-colors">
                        <div className="flex items-start gap-3">
                          <span className="w-5 h-5 border border-white/10 flex items-center justify-center font-sans text-xs text-cinema-muted shrink-0 mt-0.5 font-medium">
                            {lesson.lesson_number || lessonIndex + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-sans text-xs sm:text-sm text-white font-medium mb-0.5">{lesson.title}</p>
                            {lesson.description && (
                              <p className="font-sans text-xs text-cinema-muted mb-1.5 line-clamp-2">{stripMarkdown(lesson.description)}</p>
                            )}
                            <div className="flex flex-wrap gap-2.5">
                              {lesson.content_type && (
                                <span className="font-sans text-xs text-cinema-muted flex items-center gap-1">
                                  <FileText size={11} /> {lesson.content_type}
                                </span>
                              )}
                              {lesson.duration_minutes && (
                                <span className="font-sans text-xs text-cinema-muted flex items-center gap-1">
                                  <Clock size={11} /> {lesson.duration_minutes}m
                                </span>
                              )}
                              {lesson.assignments && lesson.assignments.length > 0 && (
                                <span className="font-sans text-xs text-purple-400 flex items-center gap-1 font-medium">
                                  <Target size={11} /> {lesson.assignments.length} assignment{lesson.assignments.length !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>

                            {lesson.assignments && lesson.assignments.length > 0 && (
                              <div className="mt-2.5 pl-3 border-l border-cinema-glow/20 space-y-2">
                                {lesson.assignments.map((assignment) => (
                                  <div key={assignment.id} className="flex items-start gap-2">
                                    <CheckCircle size={12} className="text-cinema-muted/60 mt-0.5 shrink-0" />
                                    <div>
                                      <p className="font-sans text-xs sm:text-sm text-cinema-soft font-medium">{assignment.title}</p>
                                      {assignment.description && (
                                        <p className="font-sans text-xs text-cinema-muted mt-0.5 line-clamp-1">{stripMarkdown(assignment.description)}</p>
                                      )}
                                      <div className="flex flex-wrap gap-2.5 mt-1 text-xs text-cinema-muted">
                                        <span>Type: {assignment.assignment_type}</span>
                                        <span>Max: {assignment.max_score}</span>
                                        {assignment.due_date && (
                                          <span className="flex items-center gap-1">
                                            <Calendar size={11} /> {new Date(assignment.due_date).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
